"use client";
import { useEffect, useState } from "react";
import { useCookieConsent } from "@/components/CookieContext";

export default function CookieBanner({
  testMode = false,
}: {
  testMode?: boolean;
}) {
  const [show, setShow] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [lang, setLang] = useState<"pt" | "en">("pt");
  const [marketing, setMarketing] = useState(true);
  const [analytics, setAnalytics] = useState(true);

  const { setCookiesAccepted } = useCookieConsent();

  const texts = {
    pt: {
      title: "Valorizamos sua privacidade",
      description:
        "Usamos cookies para marketing e análises. Você pode aceitar todos ou gerenciar suas preferências.",
      accept: "Aceitar Todos",
      manage: "Gerenciar",
      manageTitle: "Gerenciar Cookies",
      manageDesc: "Escolha quais cookies deseja ativar.",
      save: "Salvar Preferências",
      essential: "Cookies Essenciais (Obrigatórios)",
      marketing: "Cookies de Marketing",
      analytics: "Cookies de Análise",
      close: "Fechar",
    },
    en: {
      title: "We value your privacy",
      description:
        "We use cookies for marketing and analytics. You can accept all or manage preferences.",
      accept: "Accept All",
      manage: "Manage",
      manageTitle: "Manage Cookies",
      manageDesc: "Choose which cookies you want to enable.",
      save: "Save Preferences",
      essential: "Essential Cookies (Required)",
      marketing: "Marketing Cookies",
      analytics: "Analytics Cookies",
      close: "Close",
    },
  };

  useEffect(() => {
    const userLang = navigator.language || navigator.languages[0];
    setLang(userLang.startsWith("pt") ? "pt" : "en");

    if (testMode || !localStorage.getItem("cookiesAccepted")) {
      setShow(true);
    }

    setMarketing(localStorage.getItem("marketingCookies") !== "false");
    setAnalytics(localStorage.getItem("analyticsCookies") !== "false");
  }, [testMode]);

  const acceptCookies = () => {
    localStorage.setItem("cookiesAccepted", "true");
    localStorage.setItem("marketingCookies", "true");
    localStorage.setItem("analyticsCookies", "true");

    document.cookie = `cookiesAccepted=true; path=/; max-age=${
      60 * 60 * 24 * 365
    }`;
    document.cookie = `marketingCookies=true; path=/; max-age=${
      60 * 60 * 24 * 365
    }`;
    document.cookie = `analyticsCookies=true; path=/; max-age=${
      60 * 60 * 24 * 365
    }`;

    setCookiesAccepted(true);
    setShow(false);
  };

  const savePreferences = () => {
    localStorage.setItem("cookiesAccepted", "true");
    localStorage.setItem("marketingCookies", marketing.toString());
    localStorage.setItem("analyticsCookies", analytics.toString());

    document.cookie = `cookiesAccepted=true; path=/; max-age=${
      60 * 60 * 24 * 365
    }`;
    document.cookie = `marketingCookies=${marketing}; path=/; max-age=${
      60 * 60 * 24 * 365
    }`;
    document.cookie = `analyticsCookies=${analytics}; path=/; max-age=${
      60 * 60 * 24 * 365
    }`;

    setCookiesAccepted(true);
    setShow(false);
    setShowManage(false);
  };

  if (!show) return null;

  return (
    <>
      <div
        style={{
          position: "fixed",
          bottom: 20,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#69223c",
          color: "#fff",
          padding: "20px 25px",
          borderRadius: "12px",
          boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12px",
          maxWidth: "500px",
          width: "90%",
          fontFamily: "sans-serif",
          zIndex: 9999,
        }}
      >
        <h2 style={{ margin: 0, fontSize: "1.3rem", textAlign: "center" }}>
          {texts[lang].title}
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: "1rem",
            textAlign: "center",
            lineHeight: "1.4",
          }}
        >
          {texts[lang].description}
        </p>

        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <button
            onClick={acceptCookies}
            style={{
              background: "#ffffff",
              color: "#69223c",
              border: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              cursor: "pointer",
              fontWeight: 600,
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "#d1ceceff")
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = "#ffffff")}
          >
            {texts[lang].accept}
          </button>

          <button
            onClick={() => setShowManage(true)}
            style={{
              background: "transparent",
              color: "#fff",
              border: "1px solid #fff",
              borderRadius: "8px",
              padding: "10px 20px",
              cursor: "pointer",
              fontWeight: 600,
              transition:
                "background 0.3s ease, color 0.3s ease, transform 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(105, 34, 60, 0.85)"; // marsala translúcido
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.transform = "scale(1.05)"; // leve aumento de tamanho
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            {texts[lang].manage}
          </button>
        </div>
      </div>

      {showManage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "30px 25px",
              borderRadius: "12px",
              maxWidth: "450px",
              width: "100%",
              color: "#333",
              boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
              fontFamily: "sans-serif",
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: "1.4rem",
                color: "#69223c",
                textAlign: "center",
              }}
            >
              {texts[lang].manageTitle}
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "1rem",
                textAlign: "center",
                color: "#555",
              }}
            >
              {texts[lang].manageDesc}
            </p>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  color: "#555",
                }}
              >
                <input type="checkbox" checked disabled />
                {texts[lang].essential}
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  color: "#555",
                }}
              >
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                />
                {texts[lang].marketing}
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  color: "#555",
                }}
              >
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                />
                {texts[lang].analytics}
              </label>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "20px",
              }}
            >
              <button
                onClick={savePreferences}
                style={{
                  background: "#69223c",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "10px 20px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#4d1a36")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#69223c")
                }
              >
                {texts[lang].save}
              </button>

              <button
                onClick={() => setShowManage(false)}
                style={{
                  background: "transparent",
                  color: "#69223c",
                  border: "1px solid #69223c",
                  borderRadius: "8px",
                  padding: "10px 20px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#69223c";
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#69223c";
                }}
              >
                {texts[lang].close}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
